import React from 'react';
import { Link } from 'react-router-dom';
import LegalClause from './LegalClause';

/**
 * Terms of Service body content — no page chrome, no outer width
 * constraint, so it can be dropped into either the full /terms page
 * (pages/TermsPage.tsx) or LegalModal's scrollable popup.
 *
 * Content is transcribed from docs/legal/terms-of-service.md — that
 * file is the source of truth. If you edit the wording, edit both, and
 * bump `backend/app/constants/legal.py:CURRENT_TERMS_VERSION` if the
 * change is material (that forces re-consent — see LegalGate).
 *
 * `onOpenPrivacy`: when set (i.e. rendered inside LegalModal), the
 * cross-reference to the Privacy Policy switches the modal's active
 * document instead of navigating away and losing the modal context.
 * `onOpenRefund` does the same for clause 10's Cancellation and Refund
 * Policy link — the homepage's Trust Center tabs pass both, so no link in
 * the document leaves the homepage.
 */
interface TermsContentProps {
  onOpenPrivacy?: () => void;
  onOpenRefund?: () => void;
}

export default function TermsContent({ onOpenPrivacy, onOpenRefund }: TermsContentProps) {
  const privacyLink = onOpenPrivacy ? (
    <button
      type="button"
      onClick={onOpenPrivacy}
      className="text-legal-green-700 dark:text-legal-green-400 underline"
    >
      Privacy Policy
    </button>
  ) : (
    <Link to="/privacy" className="text-legal-green-700 dark:text-legal-green-400 underline">
      Privacy Policy
    </Link>
  );
  const refundLink = onOpenRefund ? (
    <button
      type="button"
      onClick={onOpenRefund}
      className="text-legal-green-700 dark:text-legal-green-400 underline"
    >
      Cancellation and Refund Policy
    </button>
  ) : (
    <Link to="/refund-policy" className="text-legal-green-700 dark:text-legal-green-400 underline">
      Cancellation and Refund Policy
    </Link>
  );

  return (
    <>
      <p className="text-xs tracking-[0.14em] uppercase text-legal-green-700 dark:text-legal-green-400 mb-2">
        Terms of Service
      </p>
      <h1 className="text-3xl font-semibold text-text-primary mb-3">iAdvocate Terms of Service</h1>
      <p className="text-sm text-text-secondary mb-8">
        Operated by <strong>Kovai Digi Tech</strong> under the brand <strong>iAdvocate</strong>, at{' '}
        <strong>iadvocate.ai</strong>. Last updated 3 November 2026.
      </p>

      <p className="mb-10 text-[15px] text-text-secondary">
        By creating an account, or by accessing or using the Service in any way, you agree to be
        bound by these Terms and by our {privacyLink}. If you do not agree, do not use the
        Service.
      </p>

      <LegalClause id="s1" n="1" title="Definitions">
        <p><strong>1.1 &ldquo;AI Output&rdquo;</strong> means any analysis, summary, draft, prediction, calculation, citation, or other content generated by the Service using artificial intelligence or machine learning models.</p>
        <p><strong>1.2 &ldquo;Matter Content&rdquo;</strong> means any document, text, image, or data you upload, paste, or otherwise submit in connection with a legal matter.</p>
        <p><strong>1.3 &ldquo;Client Data&rdquo;</strong> means personal data of any third party contained within Matter Content.</p>
        <p><strong>1.4 &ldquo;User&rdquo;</strong> means any individual who registers for or uses the Service.</p>
      </LegalClause>

      <LegalClause id="s2" n="2" title="Acceptance of Terms">
        <p>2.1 You must affirmatively accept these Terms and our Privacy Policy before using the Service. Acceptance is recorded against your account with the date, time, and version accepted.</p>
        <p>2.2 If we materially amend these Terms, continued use after the effective date requires you to accept the amended Terms again.</p>
      </LegalClause>

      <LegalClause id="s3" n="3" title="Eligibility">
        <p>3.1 You must be at least 18 years old and legally capable of entering into a binding contract under the Indian Contract Act, 1872.</p>
        <p>3.2 The Service is designed for legal professionals. You represent you are using it in a professional or professional-training capacity and, where relevant, that you are duly enrolled and authorised to practice law under the Advocates Act, 1961, or acting under the direct supervision of someone who is.</p>
      </LegalClause>

      <LegalClause id="s4" n="4" title="Nature of the Service — Not a Law Firm">
        <p>4.1 <strong>Kovai Digi Tech is a technology business, not a law firm.</strong> Nothing on the Service, and no AI Output, constitutes legal advice or a legal service under the Advocates Act, 1961.</p>
        <p>4.2 The Service does not itself provide legal representation to any person.</p>
        <p>4.3 Any decision to rely on, act upon, file, or communicate to a client any AI Output is <strong>solely your own professional decision</strong>.</p>
      </LegalClause>

      <LegalClause id="s5" n="5" title="No Attorney-Client Relationship">
        <p>5.1 Your use of the Service does not create an attorney-client relationship between you and Kovai Digi Tech, nor between your clients and Kovai Digi Tech.</p>
        <p>5.2 If you use the Service on behalf of a client, that relationship exists solely between you and your client.</p>
      </LegalClause>

      <LegalClause id="s6" n="6" title="AI Output: Mandatory Independent Verification">
        <div className="rounded-md border-l-4 border-legal-green-600 bg-surface-subtle px-4 py-3 mb-3 text-[13px] text-text-secondary">
          Read this section carefully — it governs how you may rely on AI-generated output.
        </div>
        <p>6.1 AI Output can be incomplete, outdated, misapplied to your facts, or wrong — including citing sections or judgments that do not exist or do not say what the output claims (&ldquo;hallucination&rdquo;).</p>
        <p>6.2 Grounding flags (verified / derived / inferred) are a risk-reduction aid, <strong>not a guarantee of accuracy</strong>.</p>
        <p>6.3 <strong>You must independently verify every AI Output against primary legal sources</strong> before relying on it for any purpose — before filing, advising a client, a hearing submission, or any other action with legal consequences.</p>
        <p>6.4 The professional and ethical responsibility for any use of AI Output rests with you alone.</p>
        <p>6.5 Kovai Digi Tech disclaims all liability for inaccurate, incomplete, or misapplied AI Output, to the fullest extent permitted under Section 14.</p>
      </LegalClause>

      <LegalClause id="s7" n="7" title="User Responsibilities &amp; Professional Conduct">
        <p>7.1 Use the Service lawfully and in compliance with the Advocates Act, 1961, Bar Council of India Rules, the IT Act, 2000, the DPDP Act, 2023, and all other applicable law.</p>
        <p>7.2 Maintain the confidentiality of your account credentials.</p>
        <p>7.3 Do not misrepresent AI Output as your own independent legal analysis without disclosing, where professionally required, that it was AI-assisted and independently verified by you.</p>
        <p>7.4 Do not use the Service for any unlawful, fraudulent, or professionally unethical purpose.</p>
      </LegalClause>

      <LegalClause id="s8" n="8" title="Matter Content and Client Data You Upload">
        <p>8.1 You retain ownership of Matter Content. You grant Kovai Digi Tech a limited licence to process, store, and analyse it solely to provide the Service.</p>
        <p>8.2 Where Matter Content contains Client Data, you represent that you have the right/consent to upload it, that you remain the Data Fiduciary under the DPDP Act, and that your use complies with your own confidentiality obligations.</p>
        <p>8.3 Kovai Digi Tech does not use Matter Content or Client Data to train third-party foundation models. Our AI processing runs on Google&rsquo;s Gemini API under a paid subscription, which under Google&rsquo;s <a href="https://ai.google.dev/gemini-api/terms" target="_blank" rel="noopener noreferrer" className="text-legal-green-700 dark:text-legal-green-400 underline">Gemini API Terms of Service</a> excludes content submitted through paid quota from model training or improvement. See our Privacy Policy for full detail.</p>
      </LegalClause>

      <LegalClause id="s9" n="9" title="Prohibited Uses">
        <p>You must not: reverse-engineer the Service; build a competing product; upload content you don&rsquo;t have the right to upload; circumvent usage limits or safety controls; or use the Service unlawfully.</p>
      </LegalClause>

      <LegalClause id="s10" n="10" title="Subscription, Fees, Billing &amp; Cancellation">
        <p>10.1 Certain features require a paid subscription; plans and limits are shown in-product and subject to change on notice.</p>
        <p>10.2 Annual plans are paid once for twelve months and do not renew automatically. Monthly plans renew only where you have authorised a recurring payment, until you cancel; cancellation takes effect at period end, with no partial-period refund except where required by law. See our {refundLink}.</p>
        <p>10.3 Fees exclude applicable taxes (including GST).</p>
      </LegalClause>

      <LegalClause id="s11" n="11" title="Intellectual Property">
        <p>11.1 The Service (excluding Matter Content and your AI Output) is owned by Kovai Digi Tech or its licensors.</p>
        <p>11.2 You receive a limited, non-exclusive, non-transferable licence to use the Service for your own professional purposes.</p>
      </LegalClause>

      <LegalClause id="s12" n="12" title="Confidentiality">
        <p>Each party protects the other&rsquo;s confidential information with reasonable care, using it only to perform under these Terms.</p>
      </LegalClause>

      <LegalClause id="s13" n="13" title="Disclaimers; No Warranty">
        <p>13.1 <strong>THE SERVICE, INCLUDING ALL AI OUTPUT, IS PROVIDED &ldquo;AS IS&rdquo;</strong>, without warranty of any kind, to the maximum extent permitted by law.</p>
        <p>13.2 We do not warrant the Service will be uninterrupted, error-free, or that AI Output will be accurate or current.</p>
        <p>13.3 Nothing here excludes any liability that cannot lawfully be excluded under Indian law, including gross negligence or wilful default.</p>
      </LegalClause>

      <LegalClause id="s14" n="14" title="Limitation of Liability">
        <p>14.1 Subject to 13.3, Kovai Digi Tech&rsquo;s total liability will not exceed fees paid by you in the 12 months preceding the claim.</p>
        <p>14.2 Kovai Digi Tech is not liable for indirect, incidental, or consequential damages, including loss from reliance on AI Output without the verification required under Section 6.</p>
      </LegalClause>

      <LegalClause id="s15" n="15" title="Indemnification">
        <p>You will indemnify Kovai Digi Tech against claims arising from your breach of these Terms, unverified reliance on AI Output, Matter Content you upload, or your violation of law.</p>
      </LegalClause>

      <LegalClause id="s16" n="16" title="Suspension &amp; Termination">
        <p>16.1 We may suspend or terminate access for breach, suspected misuse, or as required by law.</p>
        <p>16.2 You may close your account at any time; Sections 6, 11, 12, 13, 14, 15, and 18 survive termination.</p>
      </LegalClause>

      <LegalClause id="s17" n="17" title="Grievance Redressal">
        <p>Under the IT (Intermediary Guidelines) Rules, 2021, grievances may be addressed to our Grievance Officer at <strong>grievances@iadvocate.ai</strong>. We will acknowledge grievances within the timelines prescribed under applicable law.</p>
      </LegalClause>

      <LegalClause id="s18" n="18" title="Governing Law &amp; Jurisdiction">
        <p>These Terms are governed by the laws of India. The courts at <strong>Coimbatore, Tamil Nadu</strong> have exclusive jurisdiction over disputes arising from these Terms.</p>
      </LegalClause>

      <LegalClause id="s19" n="19" title="Amendments &amp; Version Control">
        <p>We may amend these Terms; material changes require re-acceptance. Each version is identified by a version label recorded against your account when you accept.</p>
      </LegalClause>

      <LegalClause id="s20" n="20" title="Miscellaneous">
        <p>Severability, no waiver, restricted assignment, entire agreement (with the Privacy Policy), and force majeure apply in the usual manner.</p>
      </LegalClause>

      <LegalClause id="s21" n="21" title="Contact">
        <p>Questions about these Terms: <strong>legal@iadvocate.ai</strong></p>
      </LegalClause>
    </>
  );
}
